/**
 * ЭТАП 3 п.3 — АДМИНСКАЯ ТАБЛИЦА РЕДИРЕКТОВ НА СЕРВЕРЕ.
 *
 * ЗАЧЕМ. `juliet_redirects` — живой инструмент контент-менеджера: завести
 * 301 со старого адреса на новый или погасить адрес в 404 без релиза. В
 * LazarServer эта таблица опрашивалась ПЕРВЫМ middleware на каждый запрос
 * (`lib/routes/redirect.ts`), то есть все накопленные за годы склейки
 * держались именно ей. У SSR-приложения аналога не было: во фронте таблица
 * применялась только на клиенте (`RootEffects.checkURI` -> `history.replace`),
 * а краулер клиентского replace не видит — для него адрес отвечал 200 и
 * плодил дубль вместо того, чтобы склеиться. Разбор — в
 * `docs/SSR_LAZARSERVER_AUDIT.md`, п.1.
 *
 * АЛГОРИТМ СОВПАДАЕТ С КЛИЕНТСКИМ, А НЕ С LAZARSERVER. В LazarServer
 * выборка шла с `where: { uri_from: pathURI }`, то есть точным сравнением в
 * SQL, и regex-правила (у них в `uri_from` есть `^`) до JS-ветки просто не
 * доезжали — на проде они не работали НИКОГДА. Клиент же тянет весь список
 * и regex применяет. Воспроизводим клиентское поведение: сервер и клиент
 * обязаны решать одинаково, иначе один и тот же адрес ведёт себя по-разному
 * при заходе извне и при SPA-навигации. Побочно это чинит regex-правила,
 * которые контент-менеджер заводил, а они молча не работали.
 *
 * Порядок как в `checkURI`: перебираем весь список, побеждает ПОСЛЕДНЕЕ
 * совпадение (обычный forEach с перезаписью), точные и regex-правила в общей
 * очереди.
 *
 * КЭШ ДВУХСЛОЙНЫЙ (30.08.2026, переезд на Redis).
 *
 * Список нужен на КАЖДЫЙ документный запрос, поэтому ходить за ним в Redis
 * каждый раз тоже накладно: лишний round-trip и разбор JSON на каждый хит.
 * Отсюда два слоя.
 *
 *  - L1, память воркера: склейка параллельных запросов на один промис плюс
 *    TTL. Работает всегда, в том числе когда Redis не поднят вовсе.
 *  - L2, Redis: общий на все воркеры. Именно он убирает главную проблему
 *    прежней схемы — у каждого воркера был свой кэш, и после правки в
 *    админке разные воркеры какое-то время отвечали по-разному.
 *
 * TTL У СЛОЁВ РАЗНЫЕ, И ЭТО НАМЕРЕННО. Значение, взятое из бэка, живёт в
 * памяти полные 300 секунд: столько же держит сам бэк (`RedirectsResolver`,
 * NodeCache на 300 секунд), дольше смысла нет, а без Redis это вообще
 * единственный слой — укоротить его значило бы бить в бэк чаще, чем
 * сегодня. Значение, взятое из Redis, живёт в памяти всего
 * `REDIRECTS_SHARED_LOCAL_TTL_MS`: перечитать общий слой дёшево, а
 * суммарное отставание от админки должно остаться примерно прежним, а не
 * сложиться из двух полных TTL.
 *
 * ⚠️ `redisClient` ИМПОРТИРУЕТСЯ ДИНАМИЧЕСКИ И ПОД `import.meta.env.SSR` по
 * той же причине, что и в `ssrCache/index.ts`: этот модуль тянет не только
 * `server.ts`, но и `routing/__root.tsx`, то есть он в КЛИЕНТСКОМ графе.
 * Статический импорт затащил бы ioredis вместе с `node:net` в браузерный
 * бандл; под статическим гардом ветка в клиентской сборке мертва и
 * вырезается вместе с импортом.
 */
import API from '@api';
import type { TtlProfile } from './ssrCache/keys';

/** Сколько живёт локальный кэш списка. Столько же, сколько NodeCache бэка. */
export const REDIRECTS_TTL_MS = 300_000;

/** Сколько живёт в памяти список, ПРИШЕДШИЙ ИЗ REDIS (см. шапку файла). */
export const REDIRECTS_SHARED_LOCAL_TTL_MS = 30_000;

/** Ключ общей записи. Профиль `meta` — те же 4–5 минут, что и у бэка. */
export const REDIRECTS_CACHE_KEY = ['ssrRedirects'] as const;
const REDIRECTS_TTL_PROFILE: TtlProfile = 'meta';

/**
 * Пауза после неудачи, прежде чем пробовать снова. Без неё упавший или
 * недоступный бэк означал бы лишний проваленный запрос НА КАЖДЫЙ хит: список
 * не закэширован, и каждый следующий рендер шёл бы за ним заново.
 */
export const REDIRECTS_RETRY_AFTER_MS = 10_000;

type RedirectRule = {
  uriFrom: string;
  uriTo: string;
  isNotFound: boolean;
};

export type RedirectDecision =
  | { kind: 'redirect'; to: string }
  | { kind: 'notFound' };

type CacheEntry = {
  loadedAt: number;
  /** Свой у каждой записи: зависит от того, откуда она пришла. */
  ttlMs: number;
  rules: RedirectRule[];
};

let cache: CacheEntry | null = null;
let inFlight: Promise<RedirectRule[]> | null = null;
let failedAt = 0;

/**
 * Только для тестов и ручной инвалидации: сбросить ЛОКАЛЬНЫЙ слой. Общая
 * запись в Redis живёт своим TTL — снаружи её никто не удаляет.
 */
export const resetRedirectsCache = (): void => {
  cache = null;
  inFlight = null;
  failedAt = 0;
};

/**
 * Чтение общего слоя. `cacheGet` не бросает никогда: если Redis выключен
 * переменной, недоступен или молчит — здесь просто `null`, и мы идём в бэк.
 */
const readShared = async (): Promise<RedirectRule[] | null> => {
  // Гард ПОЛОЖИТЕЛЬНЫЙ и обёрнут вокруг всей ветки — ровно как в
  // `ssrCache/index.ts`. Так динамический импорт остаётся внутри мёртвого
  // блока, и клиентская сборка выкидывает его вместе с блоком
  if (import.meta.env.SSR) {
    const { cacheGet } = await import('./ssrCache/redisClient');
    const cached = await cacheGet<RedirectRule[]>(REDIRECTS_CACHE_KEY);
    return cached.hit && Array.isArray(cached.value) ? cached.value : null;
  }
  return null;
};

/** Запись общего слоя — fire-and-forget: ответ страницы её не ждёт. */
const writeShared = (rules: RedirectRule[]): void => {
  if (import.meta.env.SSR) {
    void import('./ssrCache/redisClient').then(({ cacheSet }) => {
      void cacheSet(REDIRECTS_CACHE_KEY, rules, REDIRECTS_TTL_PROFILE);
    });
  }
};

const fetchRules = async (): Promise<RedirectRule[]> => {
  const data = await API.getRedirects();
  return (data?.getRedirectsActive ?? [])
    .filter(item => !!item?.uri_from)
    .map(item => ({
      uriFrom: String(item.uri_from),
      uriTo: item.uri_to ?? '',
      isNotFound: item.is_not_found === 1
    }));
};

/**
 * Общий слой, затем бэк. Ответ бэка кладём в общий слой — соседним воркерам
 * он достанется уже готовым.
 */
const loadRules = async (): Promise<CacheEntry> => {
  const shared = await readShared();
  if (shared) {
    return {
      loadedAt: Date.now(),
      ttlMs: REDIRECTS_SHARED_LOCAL_TTL_MS,
      rules: shared
    };
  }
  const rules = await fetchRules();
  writeShared(rules);
  return { loadedAt: Date.now(), ttlMs: REDIRECTS_TTL_MS, rules };
};

/**
 * Список правил. Ошибку НЕ кэшируем и НЕ пробрасываем: упавший бэк не должен
 * превращать весь сайт в 500 — просто в этот раз редиректов не будет,
 * следующий запрос попробует снова.
 */
export const getRedirectRules = async (): Promise<RedirectRule[]> => {
  if (cache && Date.now() - cache.loadedAt < cache.ttlMs) {
    return cache.rules;
  }
  if (inFlight) {
    return await inFlight;
  }
  // Недавно не получилось — не долбим бэк на каждый рендер
  if (failedAt && Date.now() - failedAt < REDIRECTS_RETRY_AFTER_MS) {
    return cache?.rules ?? [];
  }
  inFlight = loadRules()
    .then(entry => {
      cache = entry;
      failedAt = 0;
      return entry.rules;
    })
    .catch(error => {
      failedAt = Date.now();
      console.log('[ssr-redirects] не удалось получить список:', error);
      // Протухший список лучше пустого: адреса продолжат склеиваться
      return cache?.rules ?? [];
    })
    .finally(() => {
      inFlight = null;
    });
  return await inFlight;
};

/** Правило для пути. `null` — правила нет, обрабатываем адрес как обычно. */
export const matchRedirect = (
  rules: RedirectRule[],
  pathname: string
): RedirectDecision | null => {
  let matched: RedirectRule | null = null;

  for (const rule of rules) {
    if (rule.uriFrom.includes('^')) {
      try {
        if (new RegExp(rule.uriFrom).test(pathname)) {
          matched = rule;
        }
      } catch {
        // Битую регулярку из админки молча пропускаем: одно кривое правило
        // не должно ронять обработку всех остальных
      }
    } else if (rule.uriFrom === pathname) {
      matched = rule;
    }
  }

  if (!matched) {
    return null;
  }
  if (matched.isNotFound) {
    return { kind: 'notFound' };
  }
  // Пустая цель и правило A -> A: защита от зацикливания, та же, что на
  // клиенте
  if (!matched.uriTo || matched.uriTo === pathname) {
    return null;
  }
  return { kind: 'redirect', to: matched.uriTo };
};

/** Решение по конкретному пути (с походом за списком при холодном кэше). */
export const resolveRedirect = async (
  pathname: string
): Promise<RedirectDecision | null> =>
  matchRedirect(await getRedirectRules(), pathname);
